import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Search, User, Power, Pencil, Loader2, Users, Phone, Target,
} from "lucide-react";

const FILTERS = [
  { key: "ativo", label: "Ativos", testid: "status-filter-active" },
  { key: "inativo", label: "Inativos", testid: "status-filter-inactive" },
  { key: "todos", label: "Todos", testid: "status-filter-all" },
];

export default function StudentList({ onEdit, refreshKey }) {
  const [students, setStudents] = useState([]);
  const [filter, setFilter] = useState("ativo");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/students", {
        params: { status: filter === "todos" ? "todos" : filter, search },
      });
      setStudents(data);
    } catch (_) {
      toast.error("Falha ao carregar alunos");
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, refreshKey]);

  const toggleStatus = async (s) => {
    try {
      await api.patch(`/students/${s.id}/status`);
      toast.success(s.status === "ativo" ? "Aluno marcado como Inativo" : "Aluno reativado");
      load();
    } catch (_) {
      toast.error("Falha ao alterar status");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input data-testid="student-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou apelido..."
            className="h-12 pl-10 bg-[#181F2C] border-[#263147] text-slate-100 rounded-xl focus-visible:ring-emerald-500" />
        </div>
        <div className="flex gap-1 bg-[#181F2C] border border-[#263147] rounded-xl p-1">
          {FILTERS.map((f) => (
            <button key={f.key} data-testid={f.testid} onClick={() => setFilter(f.key)}
              className={`px-4 h-10 rounded-lg text-sm font-medium transition-colors ${
                filter === f.key ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-emerald-500 animate-spin" /></div>
      ) : students.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Nenhum aluno encontrado.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="student-list">
          {students.map((s) => {
            const photoUrl = s.photo_path ? `${process.env.REACT_APP_BACKEND_URL}/api/photo/${s.photo_path}` : null;
            return (
              <div key={s.id} data-testid={`student-card-${s.id}`}
                className="bg-[#181F2C] border border-[#263147] rounded-2xl p-5 flex gap-4 hover:border-[#34435f] transition-colors">
                <div className="w-14 h-14 rounded-2xl bg-[#0F1520] border border-[#263147] overflow-hidden flex items-center justify-center shrink-0">
                  {photoUrl ? <img src={photoUrl} alt={s.nickname} className="w-full h-full object-cover" />
                    : <User className="w-6 h-6 text-slate-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-100 truncate">{s.full_name}</h3>
                    <Badge className={s.status === "ativo"
                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/15"
                      : "bg-slate-500/15 text-slate-400 border-slate-500/30 hover:bg-slate-500/15"}>
                      {s.status === "ativo" ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-400">"{s.nickname}"</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</span>
                    <span className="flex items-center gap-1"><Target className="w-3 h-3" />{s.weekly_frequency}</span>
                  </div>
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {s.goals?.map((g) => (
                      <span key={g} className="text-[11px] px-2 py-0.5 rounded-md bg-[#0F1520] border border-[#263147] text-slate-400">{g}</span>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => onEdit(s)}
                      className="h-9 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-lg">
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar
                    </Button>
                    <Button size="sm" variant="outline" data-testid="student-status-toggle-button" onClick={() => toggleStatus(s)}
                      className={`h-9 rounded-lg border-[#263147] bg-[#0F1520] hover:bg-[#1E2738] ${
                        s.status === "ativo" ? "text-amber-400" : "text-emerald-400"
                      }`}>
                      <Power className="w-3.5 h-3.5 mr-1.5" /> {s.status === "ativo" ? "Inativar" : "Ativar"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
